import { useMemo } from 'react';

import { usePermissions } from '@klareospace/pharos-lib-front-utils';

import { useHandlingUnitTypes } from '&/features/settings/handling-unit-types';

// ─── Constants ────────────────────────────────────────────────────────────────

const HANDLING_UNIT_TYPES_PAGE_SIZE = 100;

// ─── Types ────────────────────────────────────────────────────────────────────

export type HandlingUnitTypeLabelResolver = (type: string) => string;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns a resolver that maps a handling-unit `type` code to its configured
 * label, falling back to the raw code when no matching type is found (or when
 * the current user lacks the `handling-unit-types:list` permission, in which
 * case the fetch is skipped entirely).
 */
export function useHandlingUnitTypeLabels(): HandlingUnitTypeLabelResolver {
  const { hasPermission } = usePermissions();
  const canListHandlingUnitTypes = hasPermission('handling-unit-types:list');

  const { data } = useHandlingUnitTypes({
    enabled: canListHandlingUnitTypes,
    page: 1,
    pageSize: HANDLING_UNIT_TYPES_PAGE_SIZE,
  });

  const labelsByCode = useMemo(() => {
    const items = data?.items ?? [];
    return new Map(items.map((handlingUnitType) => [handlingUnitType.code, handlingUnitType.label]));
  }, [data]);

  return useMemo(
    () =>
      (type: string): string =>
        labelsByCode.get(type) ?? type,
    [labelsByCode],
  );
}
